/**
 * Utility para manejo de WhatsApp
 */

/**
 * Genera el mensaje de WhatsApp para consulta de producto
 */
export const generateProductWhatsAppMessage = (product, provider, selectedColor, selectedSize, quantity, price, shareUrl) => {
    // Formatear precio - forzar formato colombiano sin decimales
    let formattedPrice = 'Consultar precio';
    if (price !== null && price !== undefined) {
        const numPrice = parseFloat(price);
        if (!isNaN(numPrice)) {
            formattedPrice = `$${numPrice.toLocaleString('es-CO', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
                useGrouping: true
            })}`;
        }
    }

    // Construir información de variantes - evitar strings vacíos
    let variantInfo = '';
    const hasColor = selectedColor && selectedColor.trim() !== '';
    const hasSize = selectedSize && selectedSize.trim() !== '';

    if (hasColor || hasSize) {
        variantInfo = '\n';
        if (hasColor) {
            variantInfo += `- Color: ${selectedColor}\n`;
        }
        if (hasSize) {
            variantInfo += `- Talla: ${selectedSize}\n`;
        }
    }

    const message = `👋 ¡Hola!
Vi este producto en Minymol y me interesa más información:

*${product.name}*
- Precio en la plataforma: ${formattedPrice}${variantInfo}

¿Está disponible? Gracias.

*Ver producto:*
${shareUrl}`;

    return message;
};

/**
 * Abre WhatsApp para consulta de producto
 */
export const openProductWhatsApp = async (product, provider, selectedColor, selectedSize, quantity, price, mainImage) => {
    if (!provider.telefono && !provider.phone) {
        throw new Error('El proveedor no tiene número de teléfono registrado');
    }

    // PASO 1: Abrir ventana INMEDIATAMENTE (mantiene el user gesture para iOS Safari)
    const whatsappWindow = window.open('', '_blank');

    // PASO 2: Mostrar feedback mientras carga
    if (whatsappWindow) {
        whatsappWindow.document.write(`
            <html>
                <head>
                    <title>Abriendo WhatsApp...</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            height: 100vh;
                            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            color: #495057;
                        }
                        .container {
                            text-align: center;
                            padding: 40px 20px;
                            background: white;
                            border-radius: 20px;
                            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                            max-width: 300px;
                            margin: 20px;
                        }
                        .whatsapp-icon {
                            width: 60px;
                            height: 60px;
                            background: #25D366;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin: 0 auto 20px;
                            animation: pulse 2s infinite;
                        }
                        .whatsapp-icon::after {
                            content: "💬";
                            font-size: 28px;
                            filter: grayscale(1) brightness(10);
                        }
                        .spinner {
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background: #25D366;
                            margin: 20px auto;
                            animation: dot-flashing 1s infinite linear alternate;
                            animation-delay: 0.5s;
                            position: relative;
                        }
                        .spinner::before,
                        .spinner::after {
                            content: '';
                            display: inline-block;
                            position: absolute;
                            top: 0;
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background: #25D366;
                            animation: dot-flashing 1s infinite alternate;
                        }
                        .spinner::before {
                            left: -8px;
                            animation-delay: 0s;
                        }
                        .spinner::after {
                            left: 8px;
                            animation-delay: 1s;
                        }
                        h2 {
                            font-size: 20px;
                            font-weight: 600;
                            margin-bottom: 8px;
                            color: #343a40;
                        }
                        p {
                            font-size: 14px;
                            color: #6c757d;
                            line-height: 1.4;
                        }
                        @keyframes pulse {
                            0% { transform: scale(1); }
                            50% { transform: scale(1.05); }
                            100% { transform: scale(1); }
                        }
                        @keyframes dot-flashing {
                            0% { opacity: 0.3; }
                            50%, 100% { opacity: 1; }
                        }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="whatsapp-icon"></div>
                        <h2>Abriendo WhatsApp</h2>
                        <p>Preparando tu consulta...</p>
                        <div class="spinner"></div>
                    </div>
                </body>
            </html>
        `);
        whatsappWindow.document.close();
    }

    try {
        // PASO 3: Generar enlace compartible (esto toma tiempo)
        const API_BASE_URL = 'https://api.minymol.com';
        const title = product.name;
        const imageUrl = mainImage;
        const redirectUrl = `https://minymol.com/product/${product.id}`;

        const shareRes = await fetch(`${API_BASE_URL}/share`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ title, imageUrl, redirectUrl }),
        });

        const shareJson = await shareRes.json();

        const shareUrl = (shareRes.ok && shareJson?.shareUrl)
            ? shareJson.shareUrl
            : redirectUrl;

        // PASO 4: Construir mensaje y URL de WhatsApp
        const message = generateProductWhatsAppMessage(product, provider, selectedColor, selectedSize, quantity, price, shareUrl);
        const encodedMessage = encodeURIComponent(message);

        // Limpiar el número de teléfono
        let rawPhone = provider.telefono || provider.phone;
        let cleanPhone = rawPhone.replace(/[^\d+]/g, '');

        let phoneNumber;
        if (cleanPhone.startsWith('+')) {
            phoneNumber = cleanPhone.substring(1);
        } else {
            phoneNumber = `57${cleanPhone}`;
        }

        // Android usa api.whatsapp.com, iOS y PC usan wa.me
        const isAndroid = /Android/i.test(navigator.userAgent);
        const whatsappUrl = isAndroid
            ? `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`
            : `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

        // PASO 5: Actualizar la ventana ya abierta con WhatsApp
        if (whatsappWindow && !whatsappWindow.closed) {
            whatsappWindow.location.href = whatsappUrl;

            // Cerrar la ventana automáticamente después de 3 segundos
            setTimeout(() => {
                if (whatsappWindow && !whatsappWindow.closed) {
                    whatsappWindow.close();
                }
            }, 3000);
        }

    } catch (error) {
        // Si algo falla, usar URL básica
        const fallbackUrl = `https://minymol.com/product/${product.id}`;
        const message = generateProductWhatsAppMessage(product, provider, selectedColor, selectedSize, quantity, price, fallbackUrl);
        const encodedMessage = encodeURIComponent(message);

        let rawPhone = provider.telefono || provider.phone;
        let cleanPhone = rawPhone.replace(/[^\d+]/g, '');
        let phoneNumber = cleanPhone.startsWith('+')
            ? cleanPhone.substring(1)
            : `57${cleanPhone}`;

        if (whatsappWindow && !whatsappWindow.closed) {
            const isAndroid = /Android/i.test(navigator.userAgent);
            const fallbackUrl = isAndroid
                ? `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`
                : `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
            whatsappWindow.location.href = fallbackUrl;
        }
    } finally {
        // Garantizar que la ventana se cierre SIEMPRE después de 3 segundos
        setTimeout(() => {
            if (whatsappWindow && !whatsappWindow.closed) {
                whatsappWindow.close();
            }
        }, 3000);
    }
};

/**
 * Genera el mensaje de WhatsApp para un abono
 */
export const generateWhatsAppMessage = (provider, amount, description, previousDebt) => {
    const newDebt = previousDebt - amount;

    const message = `Hola ${provider.name}!

Te confirmo que hoy registré un abono por $${amount.toLocaleString('es-CO')}${description ? ` (${description})` : ''}.

RESUMEN DE CUENTA:
• Abono: $${amount.toLocaleString('es-CO')}
• Saldo anterior: $${previousDebt.toLocaleString('es-CO')}
• Nuevo saldo: $${newDebt.toLocaleString('es-CO')}

${newDebt === 0 ? 'CUENTA SALDADA COMPLETAMENTE!' : 'Saldo pendiente actualizado'}

Fecha: ${new Date().toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })}

Gracias por tu confianza.`;

    return message;
};

/**
 * Abre WhatsApp con el mensaje predefinido
 */
export const openWhatsApp = (provider, amount, description, previousDebt) => {
    if (!provider.phone) {
        throw new Error('El proveedor no tiene número de teléfono registrado');
    }

    const message = generateWhatsAppMessage(provider, amount, description, previousDebt);
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = `57${provider.phone}`; // Código de Colombia
    
    // Android usa api.whatsapp.com, iOS y PC usan wa.me
    const isAndroid = /Android/i.test(navigator.userAgent);
    const whatsappUrl = isAndroid
        ? `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`
        : `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // En Android, usar location.href para evitar bloqueos
    if (isAndroid) {
        window.location.href = whatsappUrl;
    } else {
        window.open(whatsappUrl, '_blank');
    }
};

/**
 * Abre WhatsApp para un catálogo privado con patrón optimizado para iOS Safari
 * Mantiene el user gesture abriendo la ventana inmediatamente
 */
export const openCatalogWhatsApp = async (cart, providerId, catalogInfo) => {
    if (!catalogInfo?.telefono) {
        throw new Error('El proveedor no tiene número de teléfono registrado');
    }

    // PASO 1: Abrir ventana INMEDIATAMENTE (mantiene el user gesture para iOS Safari)
    const whatsappWindow = window.open('', '_blank');

    // PASO 2: Mostrar feedback mientras carga
    if (whatsappWindow) {
        whatsappWindow.document.write(`
            <html>
                <head>
                    <title>Creando pedido...</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                            height: 100vh;
                            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            align-items: center;
                            color: #495057;
                        }
                        .container {
                            text-align: center;
                            padding: 40px 20px;
                            background: white;
                            border-radius: 20px;
                            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                            max-width: 300px;
                            margin: 20px;
                        }
                        .whatsapp-icon {
                            width: 60px;
                            height: 60px;
                            background: #25D366;
                            border-radius: 50%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            margin: 0 auto 20px;
                            animation: pulse 2s infinite;
                        }
                        .whatsapp-icon::after {
                            content: "🛍️";
                            font-size: 28px;
                        }
                        .spinner {
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background: #25D366;
                            margin: 20px auto;
                            animation: dot-flashing 1s infinite linear alternate;
                            animation-delay: 0.5s;
                            position: relative;
                        }
                        .spinner::before,
                        .spinner::after {
                            content: '';
                            display: inline-block;
                            position: absolute;
                            top: 0;
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background: #25D366;
                            animation: dot-flashing 1s infinite alternate;
                        }
                        .spinner::before {
                            left: -8px;
                            animation-delay: 0s;
                        }
                        .spinner::after {
                            left: 8px;
                            animation-delay: 1s;
                        }
                        h2 {
                            font-size: 20px;
                            font-weight: 600;
                            margin-bottom: 8px;
                            color: #343a40;
                        }
                        p {
                            font-size: 14px;
                            color: #6c757d;
                            line-height: 1.4;
                        }
                        @keyframes pulse {
                            0% { transform: scale(1); }
                            50% { transform: scale(1.05); }
                            100% { transform: scale(1); }
                        }
                        @keyframes dot-flashing {
                            0% { opacity: 0.3; }
                            50%, 100% { opacity: 1; }
                        }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="whatsapp-icon"></div>
                        <h2>Creando pedido</h2>
                        <p>Generando enlace compartible...</p>
                        <div class="spinner"></div>
                    </div>
                </body>
            </html>
        `);
        whatsappWindow.document.close();
    }

    // Función auxiliar para separar valores concatenados con punto y coma
    const splitSpecValues = (value) => {
        if (!value || value === '') return [];
        return value.toString().split(';').filter(v => v && v.trim() !== '');
    };

    try {
        // PASO 3: Crear orden temporal
        const orderData = {
            providerId: parseInt(providerId),
            totalAmount: cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0),
            items: cart
        };

        const orderResponse = await fetch('https://api.minymol.com/temporary-orders', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderData)
        });

        const orderResult = await orderResponse.json();
        const token = orderResult.token;

        // PASO 4: Crear enlace compartible
        const shareData = {
            title: `Orden temporal #${token.substring(0, 8)}`,
            imageUrl: catalogInfo?.logo_url || 'https://cdn.minymol.com/uploads/logo192.png',
            redirectUrl: `https://ovicatalogos.com/?orden-temporal=${token}`
        };

        const shareResponse = await fetch('https://api.minymol.com/share', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(shareData)
        });

        const shareResult = await shareResponse.json();
        const shareUrl = (shareResponse.ok && shareResult?.shareUrl)
            ? shareResult.shareUrl
            : shareData.redirectUrl;

        // PASO 5: Construir mensaje de WhatsApp
        const totalAmount = cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

        // Función auxiliar para formatear especificaciones
        const formatSpecForWhatsApp = (value, type, unitPrice = null) => {
            // Lógica especial para cantidad: si es 1 y el precio es 0, significa que no se configuró
            if (type === 'quantity' && value === 1 && unitPrice !== null && (!unitPrice || unitPrice === 0)) {
                return 'Por especificar';
            }
            
            if (!value || value === '' || value === 0) {
                return 'Por especificar';
            }
            return value;
        };

        // Construir lista de productos con múltiples variantes expandidas
        const productsList = cart.map(item => {
            const colors = splitSpecValues(item.colorName);
            const sizes = splitSpecValues(item.sizeName);
            const quantities = splitSpecValues(item.quantity);
            
            // Si hay múltiples combinaciones, expandirlas
            if (colors.length > 1 || sizes.length > 1 || quantities.length > 1) {
                const maxLength = Math.max(colors.length, sizes.length, quantities.length);
                let productLines = [`${item.productName}`];
                
                for (let i = 0; i < maxLength; i++) {
                    const color = colors[i] || '';
                    const size = sizes[i] || '';
                    const quantity = quantities[i] || '1';
                    
                    // Formato: - [cantidad] [color lowercase], talla [talla]
                    let line = `- ${quantity}`;
                    if (color && color.trim() !== '') {
                        line += ` ${color.toLowerCase()}`;
                    }
                    if (size && size.trim() !== '') {
                        line += `, talla ${size}`;
                    }
                    
                    productLines.push(line);
                }
                
                // Calcular total de unidades
                const totalUnits = quantities.reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
                productLines.push(`    \nTotal ${totalUnits} unidades`);
                
                // Agregar precio si existe
                if (item.unitPrice && item.unitPrice > 0) {
                    productLines.push(`$${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u`);
                }
                
                return productLines.join('\n');
            } else {
                // Producto con una sola variante (comportamiento original mejorado)
                let productLine = `${item.productName}`;
                
                const colorText = formatSpecForWhatsApp(item.colorName, 'color');
                const sizeText = formatSpecForWhatsApp(item.sizeName, 'size');
                const quantityText = formatSpecForWhatsApp(item.quantity, 'quantity', item.unitPrice);
                
                // Solo agregar especificaciones si están definidas
                const specs = [];
                if (colorText !== 'Por especificar' && colorText !== '') {
                    specs.push(`Color: ${colorText}`);
                }
                if (sizeText !== 'Por especificar' && sizeText !== '') {
                    specs.push(`Talla: ${sizeText}`);
                }
                if (quantityText !== 'Por especificar' && quantityText !== '1') {
                    specs.push(`Cantidad: ${quantityText}`);
                }
                
                if (specs.length > 0) {
                    productLine += `\n  • ${specs.join(', ')}`;
                }
                
                // Agregar precio si existe
                if (item.unitPrice && item.unitPrice > 0) {
                    productLine += `\n$${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u`;
                }
                
                return productLine;
            }
        }).join('\n\n');

        // Calcular el número total de productos (items únicos en el cart)
        const totalProducts = cart.length;

        // Construir mensaje final
        const itemText = totalProducts === 1 ? 'producto' : 'productos';
        let message = `¡Hola!\n\nEste es mi pedido de ${totalProducts} ${itemText}:\n\n${productsList}`;
        
        // Solo mostrar total si hay precios definidos
        if (totalAmount > 0) {
            message += `\n\nTotal estimado: $${totalAmount.toLocaleString('es-CO')}`;
        } else {
            message += `\n\n💰 Precios por confirmar con el proveedor`;
        }
        
        message += `\n\nPuedes ver todos los detalles aquí: ${shareUrl}\n\n¡Gracias!`;

        // PASO 6: Limpiar número de teléfono y crear URL de WhatsApp
        let rawPhone = catalogInfo.telefono;
        let cleanPhone = rawPhone.replace(/[^\d+]/g, '');

        let phoneNumber;
        if (cleanPhone.startsWith('+')) {
            phoneNumber = cleanPhone.substring(1);
        } else {
            phoneNumber = `57${cleanPhone}`;
        }

        // Android usa api.whatsapp.com, iOS y PC usan wa.me
        const isAndroid = /Android/i.test(navigator.userAgent);
        const whatsappUrl = isAndroid
            ? `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`
            : `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

        // PASO 7: Actualizar la ventana ya abierta con WhatsApp
        if (whatsappWindow && !whatsappWindow.closed) {
            whatsappWindow.location.href = whatsappUrl;

            // Cerrar la ventana automáticamente después de 3 segundos
            setTimeout(() => {
                if (whatsappWindow && !whatsappWindow.closed) {
                    whatsappWindow.close();
                }
            }, 3000);
        }

    } catch (error) {
        console.error('Error al crear pedido:', error);

        // PASO 8: Fallback en caso de error - enviar mensaje detallado sin enlace
        if (whatsappWindow && !whatsappWindow.closed) {
            const fallbackTotal = cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
            
            // Construir lista de productos igual que en el flujo normal
            const fallbackProductsList = cart.map(item => {
                const colors = splitSpecValues(item.colorName);
                const sizes = splitSpecValues(item.sizeName);
                const quantities = splitSpecValues(item.quantity);
                
                // Si hay múltiples combinaciones, expandirlas
                if (colors.length > 1 || sizes.length > 1 || quantities.length > 1) {
                    const maxLength = Math.max(colors.length, sizes.length, quantities.length);
                    let productLines = [`*${item.productName}*`];
                    
                    for (let i = 0; i < maxLength; i++) {
                        const color = colors[i] || '';
                        const size = sizes[i] || '';
                        const quantity = quantities[i] || '1';
                        
                        // Formato: - [cantidad] [color lowercase], talla [talla]
                        let line = `- ${quantity}`;
                        if (color && color.trim() !== '') {
                            line += ` ${color.toLowerCase()}`;
                        }
                        if (size && size.trim() !== '') {
                            line += `, talla ${size}`;
                        }
                        
                        productLines.push(line);
                    }
                    
                    // Agregar precio si existe
                    if (item.unitPrice && item.unitPrice > 0) {
                        productLines.push(`  💰 $${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u`);
                    }
                    
                    return productLines.join('\n');
                } else {
                    // Producto con una sola variante
                    let productLine = `- ${item.productName}`;
                    
                    const specs = [];
                    if (item.colorName && item.colorName.trim() !== '') {
                        specs.push(`Color: ${item.colorName}`);
                    }
                    if (item.sizeName && item.sizeName.trim() !== '') {
                        specs.push(`Talla: ${item.sizeName}`);
                    }
                    if (item.quantity && item.quantity !== 1 && item.quantity !== '1') {
                        specs.push(`Cantidad: ${item.quantity}`);
                    }
                    
                    if (specs.length > 0) {
                        productLine += ` (${specs.join(', ')})`;
                    }
                    
                    // Agregar precio si existe
                    if (item.unitPrice && item.unitPrice > 0) {
                        productLine += ` - $${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u`;
                    }
                    
                    return productLine;
                }
            }).join('\n\n');

            // Calcular variantes totales
            const fallbackVariants = cart.reduce((total, item) => {
                const colors = splitSpecValues(item.colorName);
                const sizes = splitSpecValues(item.sizeName);
                const quantities = splitSpecValues(item.quantity);
                const maxVariants = Math.max(colors.length, sizes.length, quantities.length, 1);
                return total + maxVariants;
            }, 0);
            
            const fallbackItemText = fallbackVariants === 1 ? 'producto' : 'productos';
            let fallbackText = `¡Hola!\n\nEste es mi pedido de ${fallbackVariants} ${fallbackItemText}:\n\n${fallbackProductsList}`;
            
            if (fallbackTotal > 0) {
                fallbackText += `\n\nTotal estimado: $${fallbackTotal.toLocaleString('es-CO')}`;
            } else {
                fallbackText += `\n\n💰 Precios por confirmar con el proveedor`;
            }
            
            fallbackText += `\n\n¡Gracias!`;
            
            const fallbackMessage = encodeURIComponent(fallbackText);

            let rawPhone = catalogInfo.telefono;
            let cleanPhone = rawPhone.replace(/[^\d+]/g, '');
            let phoneNumber = cleanPhone.startsWith('+')
                ? cleanPhone.substring(1)
                : `57${cleanPhone}`;

            const isAndroid = /Android/i.test(navigator.userAgent);
            const fallbackWhatsappUrl = isAndroid
                ? `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${fallbackMessage}`
                : `https://wa.me/${phoneNumber}?text=${fallbackMessage}`;
            whatsappWindow.location.href = fallbackWhatsappUrl;
        }

        throw error; // Re-lanzar el error para que el componente lo maneje
    } finally {
        // Garantizar que la ventana se cierre SIEMPRE después de 3 segundos
        setTimeout(() => {
            if (whatsappWindow && !whatsappWindow.closed) {
                whatsappWindow.close();
            }
        }, 3000);
    }
};


/**
 * Prepara los datos para enviar pedido por WhatsApp (versión simple)
 * Retorna el mensaje y teléfono para que el componente abra WhatsApp con clic directo
 */
export const prepareCatalogWhatsAppData = (cart, catalogInfo, shareUrl) => {
    // Función auxiliar para separar valores concatenados
    const splitSpecValues = (value) => {
        if (!value || value === '') return [];
        return value.toString().split(';').filter(v => v && v.trim() !== '');
    };

    // Función auxiliar para formatear especificaciones
    const formatSpecForWhatsApp = (value, type, unitPrice = null) => {
        if (type === 'quantity' && value === 1 && unitPrice !== null && (!unitPrice || unitPrice === 0)) {
            return 'Por especificar';
        }
        if (!value || value === '' || value === 0) {
            return 'Por especificar';
        }
        return value;
    };

    const totalAmount = cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

    // Construir lista de productos
    const productsList = cart.map(item => {
        const colors = splitSpecValues(item.colorName);
        const sizes = splitSpecValues(item.sizeName);
        const quantities = splitSpecValues(item.quantity);
        
        if (colors.length > 1 || sizes.length > 1 || quantities.length > 1) {
            const maxLength = Math.max(colors.length, sizes.length, quantities.length);
            let productLines = [`${item.productName}`];
            
            for (let i = 0; i < maxLength; i++) {
                const color = colors[i] || '';
                const size = sizes[i] || '';
                const quantity = quantities[i] || '1';
                
                let line = `- ${quantity}`;
                if (color && color.trim() !== '') {
                    line += ` ${color.toLowerCase()}`;
                }
                if (size && size.trim() !== '') {
                    line += `, talla ${size}`;
                }
                
                productLines.push(line);
            }
            
            const totalUnits = quantities.reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
            productLines.push(`    \nTotal ${totalUnits} unidades`);
            
            if (item.unitPrice && item.unitPrice > 0) {
                productLines.push(`${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u`);
            }
            
            return productLines.join('\n');
        } else {
            let productLine = `${item.productName}`;
            
            const colorText = formatSpecForWhatsApp(item.colorName, 'color');
            const sizeText = formatSpecForWhatsApp(item.sizeName, 'size');
            const quantityText = formatSpecForWhatsApp(item.quantity, 'quantity', item.unitPrice);
            
            const specs = [];
            if (colorText !== 'Por especificar' && colorText !== '') {
                specs.push(`Color: ${colorText}`);
            }
            if (sizeText !== 'Por especificar' && sizeText !== '') {
                specs.push(`Talla: ${sizeText}`);
            }
            if (quantityText !== 'Por especificar' && quantityText !== '1') {
                specs.push(`Cantidad: ${quantityText}`);
            }
            
            if (specs.length > 0) {
                productLine += `\n  • ${specs.join(', ')}`;
            }
            
            if (item.unitPrice && item.unitPrice > 0) {
                productLine += `\n${Math.round(item.unitPrice).toLocaleString('es-CO')} c/u`;
            }
            
            return productLine;
        }
    }).join('\n\n');

    const totalProducts = cart.length;
    const itemText = totalProducts === 1 ? 'producto' : 'productos';
    let message = `¡Hola!\n\nEste es mi pedido de ${totalProducts} ${itemText}:\n\n${productsList}`;
    
    if (totalAmount > 0) {
        message += `\n\nTotal estimado: ${totalAmount.toLocaleString('es-CO')}`;
    } else {
        message += `\n\n💰 Precios por confirmar con el proveedor`;
    }
    
    message += `\n\nPuedes ver todos los detalles aquí: ${shareUrl}\n\n¡Gracias!`;

    // Limpiar número de teléfono
    let rawPhone = catalogInfo.telefono;
    let cleanPhone = rawPhone.replace(/[^\d+]/g, '');
    let phoneNumber = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : `57${cleanPhone}`;

    return {
        message,
        phoneNumber
    };
};
